import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { del, get, post } from "../lib/api";
import { useDocumentTitle, useLoad, listUrl } from "../lib/hooks";
import { COMPANY_STATUSES, SCORE_PARTS, SIZE_BANDS, formatDay, relativeDay, scoreColor } from "../lib/format";
import { signalTypeIcon, signalTypeLabel } from "../lib/leads";
import { todayISO } from "../../helpers/datetime";
import { Avatar, Badge, Button, Card, Confirm, Empty, ErrorBox, Input, Loading, Menu, MenuItem, Ring, ScoreBar, Select, Tabs, TierBadge, Toggle } from "../ui";
import { AutoField, DaysInput, DueSummary, InlineName, usePatcher } from "../components/DetailParts";
import Timeline from "../components/Timeline";
import History from "../components/History";
import TagEditor from "../components/TagEditor";
import SnoozeMenu from "../components/SnoozeMenu";
import { HiddenBanner, HiddenLabel } from "../components/Hidden";
import LeadsList from "../components/LeadsList";
import { useShell } from "../shell/context";

function ScorePart({ part, value, onSave }) {
    const [v, setV] = useState(String(value || 0));
    useEffect(() => setV(String(value || 0)), [value]);
    const commit = () => {
        const n = Math.max(0, Math.min(25, parseInt(v, 10) || 0));
        setV(String(n));
        if (n !== (value || 0)) onSave(n);
    };
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 64px", gap: 10, alignItems: "center" }}>
            <span>{part.label}</span>
            <span className="hz-bar hz-bar-wide"><span className="hz-fill-primary" style={{ width: `${((value || 0) / 25) * 100}%` }}></span></span>
            <Input small type="number" min={0} max={25} value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} aria-label={part.label} />
        </div>
    );
}

function People({ current, past }) {
    const [showPast, setShowPast] = useState(false);
    const row = (r) => (
        <Link key={r.id} to={`/crm/contacts/${r.contact_id}`} className="hz-list-item">
            <span className="hz-spacer" style={{ minWidth: 0 }}>
                <span className="hz-row" style={{ gap: 6 }}>
                    <span className="hz-name">{r.contact_name}</span>
                    <TierBadge tier={r.contact_tier} />
                    <HiddenLabel at={r.contact_hidden_at} />
                </span>
                <span className="hz-small hz-muted">
                    {r.title || "No title"}
                    {!r.is_current && r.end_date ? ` · until ${formatDay(r.end_date, { short: true })}` : ""}
                </span>
            </span>
            <span className="hz-small hz-muted hz-nowrap">{r.last_touch_at ? relativeDay(r.last_touch_at) : "Never touched"}</span>
            <i className="ri-arrow-right-s-line hz-muted"></i>
        </Link>
    );
    return (
        <>
            {current.length === 0 && <Empty small>No one works here in the CRM yet.</Empty>}
            {current.map(row)}
            {past.length > 0 && (
                <div className="hz-card-section" style={{ borderBottom: 0 }}>
                    <button type="button" className="hz-btn-link" onClick={() => setShowPast((s) => !s)}>
                        {showPast ? "Hide" : "Show"} {past.length} past {past.length === 1 ? "person" : "people"}
                    </button>
                </div>
            )}
            {showPast && past.map(row)}
        </>
    );
}

// Recent signals (last 90 days); each opens in the Signals inbox.
function Signals({ company, items, onAdd }) {
    return (
        <Card
            noBody
            title="Recent signals"
            actions={<Button size="sm" variant="soft" icon="ri-add-line" onClick={onAdd}>Add signal</Button>}
            footer={<Link to={`/crm/signals?company_id=${company.id}`} className="hz-small">All signals at {company.name} <i className="ri-arrow-right-s-line"></i></Link>}
        >
            {(!items || items.length === 0) && <Empty small>No signals in the last 90 days.</Empty>}
            {(items || []).map((s) => (
                <Link key={s.id} to={`/crm/signals?company_id=${company.id}&highlight=${s.id}`} className="hz-list-item" style={{ alignItems: "flex-start" }}>
                    <i className={signalTypeIcon(s.type)} style={{ fontSize: 16, color: "var(--hz-primary)", marginTop: 2 }}></i>
                    <div className="hz-spacer" style={{ minWidth: 0 }}>
                        <div className="hz-row-wrap" style={{ gap: 6 }}>
                            <span className="hz-strong">{signalTypeLabel(s.type)}</span>
                            <span className="hz-small hz-muted">{formatDay(s.seen_on, { short: true })}</span>
                            {s.acted_on ? <Badge color="success">Acted on</Badge> : <Badge color="warning">Not acted on</Badge>}
                        </div>
                        {s.detail && <div className="hz-small" style={{ color: "var(--hz-gray-700)" }}>{s.detail}</div>}
                    </div>
                    <i className="ri-arrow-right-s-line hz-muted"></i>
                </Link>
            ))}
        </Card>
    );
}

export default function CompanyDetail() {
    const { id } = useParams();
    const history = useHistory();
    const { version, openLog, openCreate, openLead, openSignal, toast, bump } = useShell();
    const [tab, setTab] = useState("touches");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { data: co, error, loading, reload, setData } = useLoad(() => get(`v1/crm/companies/${id}`), [id, version]);
    const settings = useLoad(() => get("v1/settings/crm"), []);
    const save = usePatcher(`v1/crm/companies/${id}`, co || {}, setData);
    useDocumentTitle(co ? co.name : "Company");

    if (loading && !co) return <Loading />;
    if (error && !co) return <ErrorBox error={error} onRetry={reload} />;
    if (!co) return null;

    const today = todayISO();
    const snoozed = co.snoozed_until && co.snoozed_until > today;
    const defaultCadence = settings.data ? settings.data.cadence_days_company : null;
    const score = co.qualification_score || 0;

    // E12: hiding only takes the company out of the Company list.
    const setHidden = async (hidden) => {
        if (await save({ hidden }, { quiet: true })) {
            toast(hidden ? `${co.name} is hidden from the Company list.` : `${co.name} is back in the Company list.`, {
                icon: hidden ? "ri-eye-off-line" : "ri-eye-line",
                action: { label: "Undo", run: () => save({ hidden: !hidden }, { quiet: true }) },
            });
        }
    };

    const remove = async () => {
        try {
            await del(`v1/crm/companies/${id}`);
            setConfirmDelete(false);
            toast(`${co.name} moved to the trash.`, {
                icon: "ri-delete-bin-line",
                action: { label: "Undo", run: () => post(`v1/crm/trash/companies/${id}/restore`).then(() => { bump(); history.push(`/crm/companies/${id}`); }).catch((e) => toast(e.message, { error: true })) },
            });
            bump();
            history.push("/crm/companies");
        } catch (e) {
            setConfirmDelete(false);
            toast(e.message, { error: true });
        }
    };

    return (
        <>
            <Link to={listUrl("/crm/companies")} className="hz-back"><i className="ri-arrow-left-s-line"></i>Companies</Link>
            <HiddenBanner kind="company" entity={co} onUnhide={() => setHidden(false)} />
            <div className="hz-detail-head">
                <Avatar name={co.name} large />
                <div className="hz-detail-id">
                    <InlineName entity={co} save={save} />
                    <div style={{ color: "var(--hz-slate-700)" }}>
                        {[co.industry, co.country, co.size_band ? `${co.size_band} people` : null].filter(Boolean).join(" · ") || "No details yet"}
                        {co.website && (
                            <>
                                {" · "}
                                <a href={co.website} target="_blank" rel="noopener noreferrer">{co.domain || co.website}</a>
                            </>
                        )}
                    </div>
                    <div className="hz-detail-meta">
                        <span className="hz-row" style={{ gap: 6 }}>
                            <span className="hz-small hz-muted">Status</span>
                            <Select small value={co.status} onChange={(e) => save({ status: e.target.value })} options={COMPANY_STATUSES} style={{ width: 140 }} />
                        </span>
                        <Toggle on={co.keep_in_touch} onChange={(v) => save({ keep_in_touch: v })}>Keep in touch</Toggle>
                        {co.keep_in_touch && (
                            <span className="hz-row" style={{ gap: 6 }}>
                                <span className="hz-small hz-muted">Every</span>
                                <DaysInput value={co.cadence_days} width={130} placeholder={defaultCadence ? `Default (${defaultCadence}d)` : "Days"} onSave={(v) => save({ cadence_days: v })} />
                            </span>
                        )}
                        {snoozed && (
                            <Badge color="secondary" pill>
                                <i className="ri-zzz-line"></i> Snoozed until {formatDay(co.snoozed_until, { short: true })}
                            </Badge>
                        )}
                    </div>
                </div>
                <div className="hz-detail-status">
                    <Ring value={score} color={scoreColor(score)} />
                    <div>
                        <div className="hz-strong">Qualification</div>
                        <div className="hz-small hz-muted" style={{ marginBottom: 6 }}>Score out of 100</div>
                        <DueSummary entity={co} notTrackedText="Turn on keep in touch to track a cadence." />
                    </div>
                </div>
                <div className="hz-row">
                    <Button variant="primary" icon="ri-chat-new-line" onClick={() => openLog({ company: { id: co.id, name: co.name } })}>Log touchpoint</Button>
                    <SnoozeMenu base="companies" entity={co} onChanged={(res) => { setData((x) => ({ ...x, ...res })); bump(); }} />
                    <Menu trigger={(open, toggle) => <Button variant="outline" iconOnly icon="ri-more-2-fill" title="More" onClick={toggle} style={{ width: 37, height: 37 }} />}>
                        <MenuItem onClick={() => openCreate("contact", { company: { id: co.id, name: co.name } })}>Add person here</MenuItem>
                        <MenuItem onClick={() => openLead({ company: { id: co.id, name: co.name } })}>New lead</MenuItem>
                        <MenuItem onClick={() => openSignal({ company: { id: co.id, name: co.name } })}>Add signal</MenuItem>
                        {!co.hidden_at && <MenuItem onClick={() => setHidden(true)}>Hide from Company list</MenuItem>}
                        <MenuItem danger onClick={() => setConfirmDelete(true)}>Delete company</MenuItem>
                    </Menu>
                </div>
            </div>

            <div className="hz-detail-cols">
                <div className="hz-detail-left">
                    <Card title="Details" actions={<span className="hz-xs hz-muted"><i className="ri-check-line"></i> Saves when you leave a field</span>}>
                        <div className="hz-form-grid">
                            <div className="hz-form-2">
                                <AutoField label="Website" field="website" entity={co} save={save} placeholder="https://" />
                                <AutoField label="Domain" field="domain" entity={co} save={save} hint="Unique; taken from the website when empty" />
                            </div>
                            <div className="hz-form-2">
                                <AutoField label="Industry" field="industry" entity={co} save={save} />
                                <AutoField label="Country" field="country" entity={co} save={save} hint="Two letters, e.g. TR" />
                            </div>
                            <AutoField label="Size" field="size_band" entity={co} save={save} options={SIZE_BANDS.map((s) => ({ value: s, label: `${s} people` }))} />
                            <AutoField label="Notes" field="notes" entity={co} save={save} multiline />
                        </div>
                    </Card>
                    <Card
                        title="Qualification"
                        actions={
                            <span className="hz-row" style={{ gap: 8 }}>
                                <ScoreBar value={score} color={scoreColor(score)} />
                                <span className="hz-strong">{score} / 100</span>
                            </span>
                        }
                    >
                        <div style={{ display: "grid", gap: 10 }}>
                            {SCORE_PARTS.map((p) => (
                                <ScorePart key={p.key} part={p} value={co[p.key]} onSave={(n) => save({ [p.key]: n }, { quiet: true })} />
                            ))}
                            <div className="hz-hint">Each part 0 to 25.</div>
                        </div>
                    </Card>
                    <Card noBody title="People" actions={<Button size="sm" variant="soft" icon="ri-add-line" onClick={() => openCreate("contact", { company: { id: co.id, name: co.name }, stay: true })}>Add person</Button>}>
                        <People current={co.contacts || []} past={co.past_contacts || []} />
                    </Card>
                    {co.linked_crawler_company_id && (
                        <Card title="LinkedIn hiring" actions={<a href={`/talent/jobs?fk_company_id=${co.linked_crawler_company_id}`} className="hz-btn hz-btn-sm hz-btn-outline">Job posts</a>}>
                            <div className="hz-counts">
                                <div>
                                    <div className="hz-stat" style={{ color: "var(--hz-success)" }}>{co.open_job_count || 0}</div>
                                    <div className="hz-stat-label">open now</div>
                                </div>
                                <div>
                                    <div className="hz-stat" style={{ color: "var(--hz-text-body)" }}>{co.total_job_count || 0}</div>
                                    <div className="hz-stat-label">seen in total</div>
                                </div>
                            </div>
                        </Card>
                    )}
                    <LeadsList
                        leads={co.open_leads}
                        onNew={() => openLead({ company: { id: co.id, name: co.name } })}
                        allPath={`/crm/leads?view=list&company_id=${co.id}&label=${encodeURIComponent(co.name)}`}
                    />
                    <Signals company={co} items={co.recent_signals} onAdd={() => openSignal({ company: { id: co.id, name: co.name } })} />
                    <Card title="Tags">
                        <TagEditor entity="companies" id={co.id} tags={co.tags || []} onChange={(tags) => { setData((x) => ({ ...x, tags })); bump(); }} />
                    </Card>
                </div>
                <div className="hz-detail-right">
                    <Card noBody>
                        <Tabs
                            active={tab}
                            onChange={setTab}
                            tabs={[
                                { key: "touches", label: "Touches", icon: "ri-chat-history-line" },
                                { key: "history", label: "History", icon: "ri-history-line" },
                            ]}
                        />
                        {tab === "touches" ? (
                            <>
                                <div className="hz-card-section hz-row" style={{ background: "var(--hz-surface-subtle)" }}>
                                    <span className="hz-small hz-muted hz-spacer">Touches with the company and with the people working here.</span>
                                    <Button size="sm" variant="soft" icon="ri-chat-new-line" onClick={() => openLog({ company: { id: co.id, name: co.name } })}>Log a touch</Button>
                                </div>
                                <Timeline path={`v1/crm/companies/${co.id}/touches`} mentionsPath={`v1/crm/companies/${co.id}/mentions`} entity={{ id: co.id, name: co.name }} context="company" version={version} />
                            </>
                        ) : (
                            <History entityType="company" entityId={co.id} version={version} />
                        )}
                    </Card>
                </div>
            </div>

            <Confirm open={confirmDelete} title="Delete company" danger confirmLabel="Delete" onConfirm={remove} onClose={() => setConfirmDelete(false)}>
                {co.name} goes to the trash. A company with current people or open leads cannot be deleted. Restore it from Settings, Trash within 30 days.
            </Confirm>
        </>
    );
}
